import React from "react";
import { Button } from "@/components/_components/button";
import { ContactSelectorProps } from "./types";
import { useContactSelection } from "./hooks/useContactSelection";
import { EmailSelector } from "./EmailSelector";
import { PhoneSelector } from "./PhoneSelector";
import { ManualContactInput } from "./ManualContactInput";
import { SubChannelSelector } from "./SubChannelSelector";

export const ContactSelector: React.FC<ContactSelectorProps> = ({
  contactInfo,
  setContactInfo,
  recipients,
  transMasterData,
  isLoadingRecipients,
  onKeyDown,
  setSelectedCandidate,
  agentType
}) => {
  const { clearSelection } = useContactSelection({
    contactInfo,
    setContactInfo,
    setSelectedCandidate
  });

  const hasRecipients = recipients && (recipients.emailIds.length > 0 || recipients.phoneNos.length > 0);
  const hasSelection = contactInfo.email || contactInfo.phone;
  const isTpmoAgent = agentType === 'TPMO';

  if (hasRecipients) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-foreground">
            Select Contact Method
          </span>
          {hasSelection && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearSelection}
              className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
            >
              Clear All
            </Button>
          )}
        </div>

        {/* Only show EmailSelector if agentType is NOT TPMO */}
        {!isTpmoAgent && (
          <EmailSelector
            contactInfo={contactInfo}
            setContactInfo={setContactInfo}
            recipients={recipients}
            transMasterData={transMasterData}
            setSelectedCandidate={setSelectedCandidate}
          />
        )}

        {/* Show OR divider only if both email and phone are available and agentType is not TPMO */}
        {!isTpmoAgent && recipients.phoneNos.length > 0 && recipients.emailIds.length > 0 && (
          <div className="text-center">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className={`w-full border-t ${hasSelection ? 'border-border' : 'border-border'}`} />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className={`px-2 bg-background ${hasSelection ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
                  {hasSelection ? 'OR (Choose One)' : 'OR'}
                </span>
              </div>
            </div>
          </div>
        )}

        <PhoneSelector
          contactInfo={contactInfo}
          setContactInfo={setContactInfo}
          recipients={recipients}
          transMasterData={transMasterData}
          setSelectedCandidate={setSelectedCandidate}
        />

        {/* Show SubChannelSelector for TPMO agent type when phone is selected */}
        <SubChannelSelector
          contactInfo={contactInfo}
          setContactInfo={setContactInfo}
          agentType={agentType}
        />
      </div>
    );
  }

  return (
    <ManualContactInput
      contactInfo={contactInfo}
      setContactInfo={setContactInfo}
      isLoadingRecipients={isLoadingRecipients}
      onKeyDown={onKeyDown}
    />
  );
};